import React, { useEffect, useRef } from "react";

interface Props {
  isPlaying: boolean;
  stationId?: string; // We use this to seed the randomizer
}

const HeaderOscilloscope: React.FC<Props> = ({
  isPlaying: _isPlaying,
  stationId: _stationId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;

    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.offsetWidth * dpr;
      canvas.height = container.offsetHeight * dpr;
      canvas.style.width = `${container.offsetWidth}px`;
      canvas.style.height = `${container.offsetHeight}px`;
      ctx.scale(dpr, dpr);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    const draw = () => {
      if (!container) return;

      const width = container.offsetWidth;
      const height = container.offsetHeight;
      const cy = height / 2;

      ctx.clearRect(0, 0, width, height);

      // --- ALWAYS IDLE STATE: STATIC NEON GLOW ---
      // As requested, the active wave animation is removed.
      // We only render the passive/idle neon line.

      ctx.beginPath();
      const gradient = ctx.createLinearGradient(0, cy, width, cy);

      // Fades out at edges, strong in center
      gradient.addColorStop(0, "rgba(34, 211, 238, 0)");
      gradient.addColorStop(0.2, "rgba(34, 211, 238, 0.05)");
      gradient.addColorStop(0.5, "rgba(34, 211, 238, 0.5)");
      gradient.addColorStop(0.8, "rgba(34, 211, 238, 0.05)");
      gradient.addColorStop(1, "rgba(34, 211, 238, 0)");

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;

      // Static Blur
      ctx.shadowBlur = 10;
      ctx.shadowColor = "rgba(34, 211, 238, 0.2)";

      ctx.moveTo(0, cy);
      ctx.lineTo(width, cy);
      ctx.stroke();

      ctx.shadowBlur = 0;

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []); // Removed dependencies to keep it static/passive

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden opacity-100"
    >
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

export default HeaderOscilloscope;
